import { FC, useEffect, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Dialog } from '@headlessui/react';
import { SearchIcon } from '@heroicons/react/outline';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import {
  adminCreateGiftBrandSeller,
  adminUpdateUser,
} from '@shared/api/user/user.controller';
import { User } from '@shared/api/user/user.interface';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useAsyncFn } from 'react-use';

import styles from './GiftBrandManagement.module.css';

export const SELLER_UPSERT_MODAL_ID = 'gift-brand-seller-user-upsert';

export const sellerBrands = (user: User): { id: string; name: string }[] =>
  (user.giftItemBrands ?? (user.giftItemBrand ? [user.giftItemBrand] : [])).map((brand) =>
    typeof brand === 'string'
      ? { id: brand, name: brand }
      : { id: brand._id, name: brand.name ?? brand._id },
  );

interface FormInputs {
  username: string;
  password: string;
  name: string;
  phoneNumber: string;
}
const inputClass =
  'w-full rounded border border-gray-300 bg-white px-3 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-500';

export const GiftBrandSellerUpsertModal: FC<{
  target: User | null;
  initialBrandIds?: string[];
  onSaved: () => void;
}> = ({ target, initialBrandIds, onSaved }) => {
  const open = useRecoilValue(simpleOpenAtomFamily(SELLER_UPSERT_MODAL_ID));
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(SELLER_UPSERT_MODAL_ID));
  const notiInstance = useRecoilValue(notificationInstanceAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);
  const brandStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const editMode = Boolean(target);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>();
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open) return;
    reset({
      username: target?.username ?? '',
      password: '',
      name: target?.info?.name ?? '',
      phoneNumber: target?.info?.phoneNumber ?? '',
    });
    setSelectedBrandIds(
      target ? sellerBrands(target).map((brand) => brand.id) : initialBrandIds ?? [],
    );
    setBrandSearch('');
    setSaveError('');
  }, [open, target, initialBrandIds, reset]);

  const [{ loading }, submit] = useAsyncFn(
    async (data: FormInputs) => {
      setSaveError('');
      if (selectedBrandIds.length === 0) {
        setSaveError('담당 브랜드를 1개 이상 선택해주세요.');
        return;
      }
      try {
        if (target) {
          await adminUpdateUser({
            _id: target._id,
            giftItemBrands: selectedBrandIds,
            ...(data.password ? { password: data.password } : {}),
            info: {
              ...(target.info ?? {}),
              name: data.name.trim(),
              phoneNumber: data.phoneNumber.trim(),
            },
          });
        } else {
          await adminCreateGiftBrandSeller({
            username: data.username.trim(),
            password: data.password,
            name: data.name.trim(),
            phoneNumber: data.phoneNumber.trim(),
            giftItemBrands: selectedBrandIds,
          });
        }
        notificateSuccess(notiInstance, '저장 완료');
        setOpen(false);
        onSaved();
      } catch (error) {
        setSaveError('저장하지 못했습니다. 입력 내용과 아이디 중복 여부를 확인해주세요.');
        console.error(error);
      }
    },
    [target, selectedBrandIds, notiInstance, onSaved, setOpen],
  );

  const knownIds = new Set(brandList.map((brand) => brand._id));
  const options = [
    ...brandList.map((brand) => ({ id: brand._id, name: brand.name })),
    ...(target ? sellerBrands(target).filter((brand) => !knownIds.has(brand.id)) : []),
  ];
  const filteredBrands = options.filter((brand) =>
    brand.name.toLowerCase().includes(brandSearch.trim().toLowerCase()),
  );
  const cancel = () => {
    if (!loading) {
      setOpen(false);
      history.back();
    }
  };

  return (
    <Modal
      openId={SELLER_UPSERT_MODAL_ID}
      className={`${styles.page} !bg-white !text-gray-900`}
    >
      <form
        onSubmit={handleSubmit((data) => {
          if (!loading) void submit(data);
        })}
        aria-label={editMode ? '배송관리자 수정' : '배송관리자 등록'}
      >
        <Dialog.Title as="h2" className="text-xl font-semibold tracking-tight">
          {editMode ? '배송관리자 수정' : '배송관리자 등록'}
        </Dialog.Title>
        <p className="mb-5 mt-2 text-sm text-gray-500">
          계정 정보와 담당할 브랜드를 설정합니다.
        </p>
        <fieldset
          disabled={loading}
          className="max-h-[60vh] min-w-0 space-y-6 overflow-y-auto px-0.5 pb-1"
        >
          <section aria-label="계정 정보" className="space-y-4">
            <h3 className="border-b border-gray-200 pb-2 text-sm font-semibold text-gray-900">
              계정 정보
            </h3>
            <div>
              <label
                htmlFor="seller-username"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                아이디
              </label>
              <input
                id="seller-username"
                {...register('username', {
                  validate: (value) =>
                    editMode || Boolean(value.trim()) || '아이디를 입력해주세요.',
                })}
                readOnly={editMode}
                autoComplete="off"
                placeholder="로그인 아이디"
                aria-invalid={Boolean(errors.username)}
                className={`${inputClass} ${editMode ? 'bg-gray-50' : ''}`}
              />
              {editMode && (
                <p className="mt-1 text-xs text-gray-500">아이디는 변경할 수 없습니다.</p>
              )}
              {errors.username && (
                <p role="alert" className="mt-1 text-xs text-red-600">
                  {errors.username.message}
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="seller-name"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  이름
                </label>
                <input
                  id="seller-name"
                  {...register('name')}
                  placeholder="담당자 이름"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="seller-phone"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  전화번호
                </label>
                <input
                  id="seller-phone"
                  {...register('phoneNumber')}
                  type="tel"
                  placeholder="01012345678"
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="seller-password"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                {editMode ? '새 비밀번호' : '비밀번호'}
              </label>
              <input
                id="seller-password"
                {...register('password', {
                  required: editMode ? false : '비밀번호를 입력해주세요.',
                })}
                type="password"
                autoComplete="new-password"
                placeholder={editMode ? '변경할 때만 입력' : '로그인 비밀번호'}
                aria-invalid={Boolean(errors.password)}
                className={inputClass}
              />
              {editMode && (
                <p className="mt-1 text-xs text-gray-500">
                  비워두면 기존 비밀번호를 유지합니다.
                </p>
              )}
              {errors.password && (
                <p role="alert" className="mt-1 text-xs text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>
          </section>
          <section aria-label="담당 브랜드 선택">
            <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                담당 브랜드 <span className="font-normal text-gray-500">· 복수 선택</span>
              </h3>
              <span className="text-xs text-gray-500">
                {selectedBrandIds.length}개 선택
              </span>
            </div>
            <div className="relative mb-2">
              <SearchIcon
                aria-hidden
                className="absolute left-3 top-3 h-4 w-4 text-gray-400"
              />
              <input
                type="search"
                aria-label="담당 브랜드 검색"
                value={brandSearch}
                onChange={(event) => setBrandSearch(event.target.value)}
                placeholder="브랜드명 검색"
                className={`${inputClass} pl-9`}
              />
            </div>
            <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
              {filteredBrands.map((brand) => (
                <label
                  key={brand.id}
                  className={`flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2.5 text-sm last:border-0 hover:bg-gray-50 ${
                    selectedBrandIds.includes(brand.id) ? 'bg-indigo-50/50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedBrandIds.includes(brand.id)}
                    onChange={() =>
                      setSelectedBrandIds((prev) =>
                        prev.includes(brand.id)
                          ? prev.filter((id) => id !== brand.id)
                          : [...prev, brand.id],
                      )
                    }
                    className="h-4 w-4 accent-indigo-600"
                  />
                  <span className="min-w-0 break-words">
                    {brand.name}
                    {!knownIds.has(brand.id) && (
                      <span className="ml-1 text-xs text-gray-500">기존 연결</span>
                    )}
                  </span>
                </label>
              ))}
              {filteredBrands.length === 0 && (
                <p className="px-3 py-5 text-center text-sm text-gray-500">
                  {brandStatus !== 'success'
                    ? '브랜드 목록을 확인할 수 없습니다.'
                    : brandSearch
                    ? '검색 결과가 없습니다.'
                    : '등록된 브랜드가 없습니다.'}
                </p>
              )}
            </div>
            {brandStatus === 'success' && brandList.length === 0 && (
              <Link
                to="/gift-seller/item-brand/new"
                onClick={() => setOpen(false)}
                className="mt-2 inline-block text-sm text-indigo-700 underline"
              >
                브랜드 등록하기
              </Link>
            )}
          </section>
          {saveError && (
            <p
              role="alert"
              className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {saveError}
            </p>
          )}
        </fieldset>
        <div className="mt-5 flex justify-end gap-2 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={cancel}
            disabled={loading}
            className="rounded border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 disabled:opacity-40"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={loading || brandStatus !== 'success'}
            className="rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {loading ? '저장 중…' : editMode ? '변경사항 저장' : '계정 등록'}
          </button>
        </div>
      </form>
      <LoadingModal open={loading} title="저장 중" />
    </Modal>
  );
};
